import React, { useCallback, useEffect, useRef, useState } from 'react'

import { CONTAINER_TYPES } from '../constants'
import { StructureTreeItem } from '../types'

export interface UseTreeExpansionOptions {
	autoExpandDepth?: number,
	childrenMap: Record<string, StructureTreeItem[]>,
	controlledExpandedItems?: string[],
	loadChildren: (parentId: string) => Promise<void>,
	onExpandedItemsChange?: (itemIds: string[]) => void,
	/** Set of system IDs - used to collapse all children only when system is closed */
	systemIds?: Set<string>,
	systems: StructureTreeItem[]
}

export interface UseTreeExpansionReturn {
	allowCollapseRef: React.MutableRefObject<boolean>,
	expandedItems: string[],
	handleExpandedItemsChange: (
		event: React.SyntheticEvent | null,
		itemIds: string[],
	) => void
	setInternalExpandedItems: React.Dispatch<React.SetStateAction<string[]>>,
}

export const useTreeExpansion = ({
	controlledExpandedItems,
	onExpandedItemsChange,
	autoExpandDepth = 0,
	systems,
	systemIds,
	childrenMap,
	loadChildren,
}: UseTreeExpansionOptions): UseTreeExpansionReturn => {
	const [internalExpandedItems, setInternalExpandedItems] = useState<string[]>([])
	const [autoExpandTriggered, setAutoExpandTriggered] = useState(false)
	const loadChildrenRef = useRef(loadChildren)

	const allowCollapseRef = useRef(false)

	const expandedItems = controlledExpandedItems ?? internalExpandedItems

	useEffect(() => {
		loadChildrenRef.current = loadChildren
	}, [loadChildren])


	const getDescendantIds = useCallback(
		(parentId: string): string[] => {
			const descendants: string[] = []
			const children = childrenMap[parentId]
			if (children) {
				children.forEach((child) => {
					descendants.push(child.id)
					descendants.push(...getDescendantIds(child.id))
				})
			}
			return descendants
		},
		[childrenMap],
	)

	const handleExpandedItemsChange = useCallback(
		(_event: React.SyntheticEvent | null, itemIds: string[]) => {
			const newlyExpanded = itemIds.filter(
				(id) => !expandedItems.includes(id),
			)
			const newlyCollapsed = expandedItems.filter(
				(id) => !itemIds.includes(id),
			)

			const hasChanges = newlyExpanded.length > 0 || newlyCollapsed.length > 0

			if (hasChanges && !allowCollapseRef.current) {
				return
			}

			allowCollapseRef.current = false

			let finalItemIds = itemIds
			if (newlyCollapsed.length > 0) {
				// Only collapse descendants when a SYSTEM is collapsed
				const collapsedSystems = newlyCollapsed.filter((id) => systemIds?.has(id))
				if (collapsedSystems.length > 0) {
					const descendantsToRemove = new Set<string>()
					collapsedSystems.forEach((collapsedId) => {
						getDescendantIds(collapsedId).forEach((id) =>
							descendantsToRemove.add(id),
						)
					})
					finalItemIds = itemIds.filter((id) => !descendantsToRemove.has(id))
				}
			}

			if (onExpandedItemsChange) {
				onExpandedItemsChange(finalItemIds)
			} else {
				setInternalExpandedItems(finalItemIds)
			}

			newlyExpanded.forEach((id) => loadChildren(id))
		},
		[expandedItems, onExpandedItemsChange, loadChildren, getDescendantIds, systemIds],
	)

	useEffect(() => {
		if (autoExpandDepth > 0 && systems.length > 0 && !autoExpandTriggered) {
			setAutoExpandTriggered(true)

			const systemIds = systems.map((s) => s.id)

			if (onExpandedItemsChange) {
				onExpandedItemsChange(systemIds)
			} else {
				setInternalExpandedItems(systemIds)
			}

			systemIds.forEach((id) => loadChildren(id))
		}
	}, [
		autoExpandDepth,
		systems,
		autoExpandTriggered,
		onExpandedItemsChange,
		loadChildren,
	])

	useEffect(() => {
		if (autoExpandDepth >= 2 && autoExpandTriggered) {
			const containersToExpand: string[] = []

			systems.forEach((system) => {
				const children = childrenMap[system.id]
				if (children) {
					children.forEach((child) => {
						if (
							CONTAINER_TYPES.includes(child.type) &&
							!expandedItems.includes(child.id)
						) {
							containersToExpand.push(child.id)
						}
					})
				}
			})

			if (containersToExpand.length > 0) {
				const newExpanded = [
					...new Set([...expandedItems, ...containersToExpand]),
				]

				if (onExpandedItemsChange) {
					onExpandedItemsChange(newExpanded)
				} else {
					setInternalExpandedItems(newExpanded)
				}

				containersToExpand.forEach((id) => loadChildren(id))
			}
		}
	}, [
		autoExpandDepth,
		autoExpandTriggered,
		systems,
		childrenMap,
		expandedItems,
		onExpandedItemsChange,
		loadChildren,
	])

	return {
		allowCollapseRef,
		expandedItems,
		handleExpandedItemsChange,
		setInternalExpandedItems,
	}
}

